import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { IntegrationKind, KindLogo } from './integrationKinds';
import { authConfig, fetchOverview } from 'services/integrations';

/**
 * Choice of integration type (`/integrations/start`): "Ajouter une intégration" from "Mes
 * intégrations", and the first screen for a seller with nothing set up ("Comment arrivent vos
 * leads ?"). Each type says what it is, who it's for and what it takes, in the seller's words —
 * never pixel / iframe / MCP. Google Sheet only exists for accounts allowed to (canSellWithGSheet),
 * and is never labelled as a special tier.
 * See notes/ux-integrations-vendeur.md §4.2.
 */

interface Choice {
    key: string;
    title: string;
    tagline: string;
    what: string;
    forWho: string;
    needs: string;
    cta: string;
    // Logo and color of the kind (integrationKinds.tsx), so the four read apart at a glance.
    kind: IntegrationKind;
    recommended?: boolean;
    onClick: () => void;
}

const IntegrationStart = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [hasIntegrations, setHasIntegrations] = useState(false);
    // Sites already linked to a site form: "Ajouter une page à un site existant" needs no new integration.
    const [linkedSites, setLinkedSites] = useState<{ templateId: string; hostname: string }[]>([]);
    const [pageSite, setPageSite] = useState('');

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/user/me`, authConfig())
            .then((res) => setUser(res.data))
            .catch(() => {});
        fetchOverview()
            .then((list) => {
                setHasIntegrations(list.length > 0);
                const sites = list.filter((t) => t.isUniversal).flatMap((t) => t.sites.map((s) => ({ templateId: t._id, hostname: s.hostname })));
                setLinkedSites(sites);
                setPageSite(sites[0] ? `${sites[0].templateId}|${sites[0].hostname}` : '');
            })
            .catch(() => {});
    }, []);

    // Nothing is created here: the "Mon formulaire Leadrs" integration only exists once the seller
    // has given the site it goes on (site.tsx).
    const startSite = () => navigate('/integrations/new-site');

    const choices: Choice[] = [
        {
            key: 'site',
            title: 'Formulaire',
            tagline: "J'ai déjà un site",
            recommended: true,
            what: 'Deux codes à coller : le formulaire recueille les demandes, on vend chaque lead à un acheteur.',
            forWho: 'WordPress, Wix, Webflow, Shopify, site codé',
            needs: 'Accès à votre site · 10 min',
            cta: 'Brancher mon site →',
            kind: 'site',
            onClick: startSite,
        },
        {
            key: 'ai',
            title: "Site créé avec l'IA",
            tagline: "Intégration MCP",
            what: 'Votre IA (Claude, Cursor…) crée le site, formulaire inclus et déjà relié à votre compte.',
            forWho: 'Un nouveau site, sans le construire',
            needs: 'Un outil IA + votre clé API',
            cta: 'Créer mon site →',
            kind: 'ai',
            onClick: () => navigate('/seller-mcp'),
        },
        {
            key: 'api',
            title: 'Envoi par API',
            tagline: 'Mes leads sont dans un outil',
            what: 'Votre CRM, formulaire maison ou Zapier / Make / n8n envoie les leads, sans formulaire Leadrs.',
            forWho: 'Leads déjà collectés ailleurs',
            needs: 'Un développeur + votre clé API',
            cta: "Voir l'envoi par API →",
            kind: 'api',
            onClick: () => navigate('/seller-api-docs'),
        },
        ...(user?.canSellWithGSheet
            ? [
                  {
                      key: 'sheet',
                      title: 'Import Google Sheet',
                      tagline: 'Depuis votre fichier',
                      what: 'Un Google Sheet relié à votre compte : chaque ligne ajoutée devient un lead vendu.',
                      forWho: 'Leads récupérés à la main ou en fichier',
                      needs: 'Un compte Google',
                      cta: 'Créer mon Google Sheet →',
                      kind: 'sheet' as const,
                      onClick: () => navigate('/integrations/new-sheet'),
                  },
              ]
            : []),
    ];

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-4 pb-16 pt-2 font-hanken text-[#1A1613]">
            {hasIntegrations && (
                <button onClick={() => navigate('/integrations')} className="self-start font-hanken text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                    ← Mes intégrations
                </button>
            )}
            <div>
                <h1 className="mt-1.5 font-newsreader text-[32px] font-medium leading-[1.1] tracking-[-0.02em] text-[#14110E]">
                    Comment arrivent vos leads ?
                </h1>
                <p className="mt-1.5 font-hanken text-[14px] text-[#6B6357]">
                    Choisissez le type d'intégration qui vous ressemble. Vous pourrez en ajouter d'autres ensuite.
                </p>
            </div>

            <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${choices.length > 3 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
                {choices.map((c) => (
                    <button
                        key={c.key}
                        onClick={c.onClick}
                        className={`group relative flex flex-col gap-2.5 rounded-[16px] border-[1.5px] p-4 text-left transition-colors hover:border-[#241C5C] disabled:opacity-60 ${
                            c.recommended ? 'border-[#241C5C] bg-[#FBFAFF]' : 'border-[#EDE7DE] bg-white'
                        }`}
                    >
                        <span className="flex items-start gap-3">
                            <KindLogo kind={c.kind} />
                            <span className={`min-w-0 ${c.recommended ? 'pr-[84px]' : ''}`}>
                                <span className="block font-hanken text-[15px] font-semibold leading-snug text-[#1A1613]">{c.title}</span>
                                <span className="block font-hanken text-[12.5px] text-[#8A8172]">« {c.tagline} »</span>
                            </span>
                        </span>
                        {c.recommended && (
                            <span className="absolute right-3 top-3 rounded-full bg-[#E8E6FB] px-2 py-[2px] font-hanken text-[11px] font-semibold text-[#312B78]">
                                Recommandé
                            </span>
                        )}
                        <span className="font-hanken text-[13px] leading-snug text-[#3B352C]">{c.what}</span>
                        <span className="flex flex-col gap-0.5 border-t border-[#F1ECE4] pt-2 font-hanken text-[12px] leading-snug text-[#6B6357]">
                            <span>
                                <strong className="font-semibold text-[#3B352C]">Pour :</strong> {c.forWho}
                            </span>
                            <span>
                                <strong className="font-semibold text-[#3B352C]">Il faut :</strong> {c.needs}
                            </span>
                        </span>
                        <span className="mt-auto pt-1 font-hanken text-[13px] font-semibold text-[#241C5C] group-hover:underline">
                            {c.cta}
                        </span>
                    </button>
                ))}
            </div>

            {linkedSites.length > 0 && (
                <div className="flex flex-wrap items-center gap-2.5 rounded-[14px] border border-[#EDE7DE] bg-white px-4 py-3">
                    <p className="min-w-0 flex-1 font-hanken text-[13.5px] text-[#3B352C]">
                        <strong>Vous avez déjà un site relié ?</strong> Ajoutez-y une page, sans nouvelle intégration.
                    </p>
                    <select
                        value={pageSite}
                        onChange={(e) => setPageSite(e.target.value)}
                        aria-label="Site"
                        className="rounded-[9px] border border-[#DDD5C9] bg-white px-2.5 py-1.5 font-hanken text-[13px] outline-none focus:border-[#241C5C]"
                    >
                        {linkedSites.map((s) => (
                            <option key={`${s.templateId}|${s.hostname}`} value={`${s.templateId}|${s.hostname}`}>
                                {s.hostname}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => {
                            const [templateId, hostname] = pageSite.split('|');
                            navigate(`/integrations/${templateId}?tab=pages&site=${encodeURIComponent(hostname)}&add=1`);
                        }}
                        className="rounded-[10px] bg-[#241C5C] px-3.5 py-2 font-hanken text-[13px] font-semibold text-white hover:bg-[#332a75]"
                    >
                        Ajouter une page →
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 rounded-[14px] border border-[#D7D3F5] bg-[#E8E6FB] px-4 py-3">
                <p className="min-w-0 flex-1 font-hanken text-[13px] text-[#4A4470]">
                    <strong className="text-[#241C5C]">Pas sûr de votre choix ?</strong> Envoyez-nous l'adresse de votre site sur WhatsApp, on
                    vous conseille et on peut l'installer avec vous (lun.–ven.).
                </p>
                <button
                    onClick={() => navigate('/assistance')}
                    className="shrink-0 rounded-[10px] border border-[#241C5C] px-3.5 py-2 font-hanken text-[13px] font-semibold text-[#241C5C] transition-colors hover:bg-white"
                >
                    Ouvrir WhatsApp
                </button>
            </div>
        </div>
    );
};

export default IntegrationStart;
